import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ShoppingCart, Minus, Plus, Search, Save, Send, Trash2, ArrowLeft, ArrowRight,
  Store, Clock, User, CalendarDays, Zap, Sparkles, PackageCheck, ClipboardList, Truck, History,
} from "lucide-react";
import api from "../lib/api";
import { PageLoader, Button, Badge, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { CATEGORY_COLORS, STOCK_STATUS, fmtUnit, fmtNum, fmtRp } from "../lib/constants";
import { ProductAvatar } from "../components/visual";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const friendlyDate = (d) => `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

const ORDER_TYPES = [
  { key: "REGULAR", label: "Regular Order", icon: ClipboardList, tone: "text-ink-soft" },
  { key: "URGENT", label: "Urgent Order", icon: Zap, tone: "text-danger" },
  { key: "SPECIAL", label: "Special Request", icon: Sparkles, tone: "text-primary" },
];

export default function OrderForm() {
  const { user } = useAuth();
  const { t: tr } = useT();
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [products, setProducts] = useState(null);
  const [stockMap, setStockMap] = useState({});
  const [branch, setBranch] = useState(null);
  const [cart, setCart] = useState({});
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [orderType, setOrderType] = useState("REGULAR");
  const [notes, setNotes] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [step, setStep] = useState("select"); // select | review
  const [orderId, setOrderId] = useState(id || null);
  const [orderNo, setOrderNo] = useState(null);
  const [meta, setMeta] = useState({ dateObj: new Date(), timeStr: "", byName: user?.name || "", updatedBy: null, updatedAt: null });
  const [cartOpen, setCartOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [recLoading, setRecLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(!!id);

  useEffect(() => {
    const now = new Date();
    setMeta((m) => ({ ...m, dateObj: now, timeStr: now.toTimeString().slice(0, 5), byName: user?.name || "" }));
    api.get("/products?active_only=true&visible_in=branch_ordering").then(({ data }) => setProducts(data));
    api.get("/my-branch-stock").then(({ data }) => setStockMap(data)).catch(() => setStockMap({}));
    api.get("/branches").then(({ data }) => {
      setBranch((data || []).find((b) => b.id === user?.branch_id) || null);
    }).catch(() => setBranch(null));
    if (!id) {
      // Consume "Tambahkan ke Pesanan" prefill from the Branch Dashboard low-stock list
      try {
        const pre = JSON.parse(localStorage.getItem("cf_order_prefill") || "[]");
        if (Array.isArray(pre) && pre.length) {
          const c = {};
          pre.forEach((e) => { if (e.product_id && Number(e.qty) > 0) c[e.product_id] = Number(e.qty); });
          if (Object.keys(c).length) setCart(c);
        }
      } catch (e) { /* ignore malformed prefill */ }
      localStorage.removeItem("cf_order_prefill");
    }
    if (id) {
      api.get(`/orders/${id}`).then(({ data }) => {
        const c = {};
        data.items.forEach((it) => { c[it.product_id] = it.ordered_quantity; });
        setCart(c);
        setOrderType(data.order_type || "REGULAR");
        setNotes(data.notes || "");
        setDeliveryDate(data.requested_delivery_date || "");
        setDeliveryTime(data.requested_delivery_time || "");
        setOrderNo(data.order_no);
        const created = data.created_at ? new Date(data.created_at) : new Date();
        setMeta({
          dateObj: data.order_date ? new Date(data.order_date + "T00:00:00") : created,
          timeStr: created.toTimeString().slice(0, 5),
          byName: data.created_by_name || user?.name || "",
          updatedBy: data.updated_by_name || null,
          updatedAt: data.updated_at || null,
        });
      }).catch(() => {}).finally(() => setLoadingOrder(false));
    }
    // eslint-disable-next-line
  }, [id]);

  const productMap = useMemo(() => Object.fromEntries((products || []).map((p) => [p.id, p])), [products]);
  const cats = useMemo(
    () => ["all", ...new Set((products || []).map((p) => p.category_name).filter(Boolean))],
    [products]
  );
  const filtered = useMemo(
    () => (products || []).filter(
      (p) => {
        if (cat !== "all" && p.category_name !== cat) return false;
        const q = search.toLowerCase();
        if (!q) return true;
        // Category-aware search: matches product name OR its category name.
        return (p.name || "").toLowerCase().includes(q) || (p.category_name || "").toLowerCase().includes(q);
      }
    ),
    [products, cat, search]
  );

  const lines = useMemo(
    () => Object.entries(cart).map(([pid, qty]) => ({ ...productMap[pid], product_id: pid, qty })).filter((l) => l.id),
    [cart, productMap]
  );
  const totals = useMemo(() => ({
    items: lines.length,
    quantity: lines.reduce((s, l) => s + l.qty, 0),
    value: lines.reduce((s, l) => s + l.qty * (l.price || 0), 0),
  }), [lines]);

  if (!products || loadingOrder) return <PageLoader />;

  const branchName = branch?.name || user?.branch?.name || "Branch";
  const friendlyNo = `${branchName} - ${friendlyDate(meta.dateObj)}`;

  const setQty = (pid, q) => setCart((c) => { const n = { ...c }; if (q <= 0) delete n[pid]; else n[pid] = q; return n; });
  const buildItems = () => Object.entries(cart).map(([product_id, quantity]) => ({ product_id, quantity }));

  const save = async (submit) => {
    if (totals.items === 0) return toast.error(tr("Add at least one product"));
    setSaving(true);
    try {
      let oid = orderId;
      const body = { items: buildItems(), notes, order_type: orderType, requested_delivery_date: deliveryDate || null, requested_delivery_time: deliveryTime || null };
      if (oid) {
        await api.put(`/orders/${oid}`, body);
      } else {
        const { data } = await api.post("/orders", { branch_id: user.branch_id, ...body });
        oid = data.id; setOrderId(oid); setOrderNo(data.order_no);
      }
      if (submit) {
        await api.post(`/orders/${oid}/submit`);
        toast.success(tr("Order submitted for approval!"));
      } else {
        toast.success(tr("Draft saved"));
      }
      navigate("/orders");
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const loadRecommendation = async () => {
    setRecLoading(true);
    try {
      const { data } = await api.get("/orders/recommendation");
      const items = data.items || [];
      if (!items.length) { toast.success(tr("Stock is healthy — no recommendation needed")); return; }
      setCart((c) => { const n = { ...c }; items.forEach((it) => { n[it.product_id] = it.recommended_qty; }); return n; });
      toast.success(`${items.length} ${tr("recommended items added")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Failed to save"));
    } finally {
      setRecLoading(false);
    }
  };

  /* ---------------- REVIEW STEP ---------------- */
  if (step === "review") {
    const otype = ORDER_TYPES.find((t) => t.key === orderType);
    return (
      <div className="animate-fade-up pb-36 lg:pb-2">
        <button onClick={() => setStep("select")} className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-ink-soft transition-colors hover:text-primary" data-testid="review-back">
          <ArrowLeft size={16} /> {tr("Back to products")}
        </button>

        <div className="mb-2 flex items-center gap-2">
          <PackageCheck size={20} className="text-primary" />
          <h1 className="font-heading text-2xl font-bold tracking-tight text-ink md:text-3xl">{tr("Review & Confirm")}</h1>
        </div>
        <p className="mb-6 text-sm text-ink-soft">{tr("Check everything below, then save as draft or submit for approval.")}</p>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            {/* Order summary card */}
            <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-card">
              <div className="flex items-center gap-4 border-b border-line bg-primary-light/60 p-5">
                {branch?.logo
                  ? <img src={branch.logo} alt="" className="h-14 w-14 rounded-2xl border border-line object-cover" />
                  : <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-white"><Store size={24} /></div>}
                <div>
                  <p className="font-heading text-xl font-bold text-ink" data-testid="review-friendly-no">{friendlyNo}</p>
                  <p className="text-xs font-semibold uppercase tracking-wider text-ink-mute">{orderNo || tr("Number assigned on save")}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 p-5 sm:grid-cols-3">
                <Meta icon={Store} label={tr("Branch")} value={branchName} />
                <Meta icon={CalendarDays} label={tr("Order Date (created)")} value={friendlyDate(meta.dateObj).replace(/-/g, " ")} />
                <Meta icon={Clock} label={tr("Order Time (created)")} value={meta.timeStr} />
                <Meta icon={User} label={tr("Ordered By")} value={meta.byName} />
                <Meta icon={otype?.icon || ClipboardList} label={tr("Order Type")} value={otype ? tr(otype.label) : ""} />
                <Meta icon={Truck} label={tr("Delivery Date")} value={deliveryDate ? friendlyDate(new Date(deliveryDate + "T00:00:00")).replace(/-/g, " ") : tr("Not specified")} />
                <Meta icon={Clock} label={tr("Delivery Time")} value={deliveryTime || tr("Not specified")} />
              </div>
            </div>

            {/* Items */}
            <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-card">
              <div className="flex items-center justify-between border-b border-line px-5 py-4">
                <h3 className="font-heading text-lg font-bold">{tr("Order Items")}</h3>
                <span className="text-sm text-ink-mute">{totals.items} {tr("item")}</span>
              </div>
              {lines.map((l) => (
                <div key={l.id} className="flex items-center gap-3 border-b border-line/60 px-5 py-3 last:border-0" data-testid={`review-item-${l.name}`}>
                  <ProductAvatar image={l.image} colorKey={l.category_color} name={l.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-ink">{l.name}</p>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-ink-mute">{tr("per")} {fmtUnit(l.unit)} · {fmtRp(l.price || 0)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-lg font-bold text-ink">{fmtNum(l.qty)} <span className="text-xs uppercase text-ink-mute">{fmtUnit(l.unit)}</span></p>
                    <p className="text-xs font-semibold text-primary">{fmtRp((l.price || 0) * l.qty)}</p>
                  </div>
                </div>
              ))}
            </div>

            {notes && (
              <div className="rounded-2xl border border-line bg-white p-5 shadow-card">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-mute">{tr("Notes")}</p>
                <p className="text-sm text-ink">{notes}</p>
              </div>
            )}

            {/* Order tracking */}
            <div className="rounded-2xl border border-line bg-white p-5 shadow-card" data-testid="order-tracking">
              <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-mute"><History size={13} /> {tr("Order Tracking")}</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                <Meta icon={User} label={tr("Created By")} value={meta.byName} />
                <Meta icon={CalendarDays} label={tr("Created Date")} value={friendlyDate(meta.dateObj).replace(/-/g, " ")} />
                <Meta icon={Clock} label={tr("Created Time")} value={meta.timeStr} />
                <Meta icon={User} label={tr("Last Updated By")} value={meta.updatedBy || "—"} />
                <Meta icon={Clock} label={tr("Last Updated")} value={meta.updatedAt ? new Date(meta.updatedAt).toLocaleString() : "—"} />
              </div>
            </div>
          </div>

          {/* Totals + actions */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4 rounded-2xl border border-line bg-white p-5 shadow-card">
              <h3 className="font-heading text-lg font-bold">{tr("Summary")}</h3>
              <Row label={tr("Total Items")} value={totals.items} testid="summary-items" />
              <Row label={tr("Total Quantity")} value={fmtNum(totals.quantity)} testid="summary-qty" />
              <div className="flex items-center justify-between border-t border-line pt-3">
                <span className="text-sm font-semibold text-ink-soft">{tr("Estimated Value")}</span>
                <span className="font-heading text-lg font-bold text-primary" data-testid="summary-value">{fmtRp(totals.value)}</span>
              </div>
              <div className="hidden grid-cols-1 gap-2 pt-2 lg:grid">
                <Button onClick={() => save(true)} loading={saving} className="w-full" data-testid="submit-order-button"><Send size={16} /> {tr("Submit Order")}</Button>
                <Button variant="outline" onClick={() => save(false)} loading={saving} className="w-full" data-testid="save-draft-button"><Save size={16} /> {tr("Save as Draft")}</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile sticky action bar */}
        <div className="fixed bottom-16 left-0 right-0 z-20 grid grid-cols-2 gap-2 border-t border-line bg-white/95 px-4 py-3 backdrop-blur-md lg:hidden" data-testid="review-mobile-actions">
          <Button variant="outline" onClick={() => save(false)} loading={saving} className="w-full" data-testid="save-draft-button-mobile"><Save size={16} /> {tr("Draft")}</Button>
          <Button onClick={() => save(true)} loading={saving} className="w-full" data-testid="submit-order-button-mobile"><Send size={16} /> {tr("Submit")}</Button>
        </div>
      </div>
    );
  }

  /* ---------------- SELECT STEP (POS) ---------------- */
  return (
    <div className="animate-fade-up pb-40 lg:pb-2">
      {/* Order Info Header */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-line bg-white shadow-card" data-testid="order-info-card">
        <div className="flex flex-col gap-4 border-b border-line bg-gradient-to-r from-primary-light to-white p-5 sm:flex-row sm:items-center">
          {branch?.logo
            ? <img src={branch.logo} alt="" className="h-16 w-16 rounded-2xl border border-line object-cover" />
            : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-white"><Store size={28} /></div>}
          <div className="flex-1">
            <p className="font-heading text-2xl font-bold tracking-tight text-ink" data-testid="order-friendly-no">{friendlyNo}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-mute">{orderNo ? orderNo : tr("New Order · number assigned on save")}</p>
          </div>
          <Badge tone="info" className="self-start sm:self-center">{orderId ? tr("Editing") : tr("New Order")}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 p-5 sm:grid-cols-4">
          <Meta icon={Store} label={tr("Branch")} value={branchName} />
          <Meta icon={CalendarDays} label={tr("Order Date (created)")} value={friendlyDate(meta.dateObj).replace(/-/g, " ")} />
          <Meta icon={Clock} label={tr("Order Time (created)")} value={meta.timeStr} />
          <Meta icon={User} label={tr("Ordered By")} value={meta.byName} />
          <div className="col-span-2">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-mute">{tr("Order Type")}</p>
            <div className="flex flex-wrap gap-2">
              {ORDER_TYPES.map((t) => {
                const Icon = t.icon;
                const active = orderType === t.key;
                return (
                  <button key={t.key} onClick={() => setOrderType(t.key)} data-testid={`ordertype-${t.key}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all ${active ? "border-primary bg-primary text-white shadow-sm" : "border-line bg-white text-ink-soft hover:border-primary/50"}`}>
                    <Icon size={15} className={active ? "text-white" : t.tone} /> {tr(t.label)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-mute"><Truck size={13} /> {tr("Delivery Date")}</p>
              <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="cf-input" data-testid="order-delivery-date" />
            </div>
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-mute"><Clock size={13} /> {tr("Delivery Time")}</p>
              <input type="time" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} className="cf-input" data-testid="order-delivery-time" />
            </div>
          </div>
          <div className="col-span-2 sm:col-span-4">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-mute">{tr("Notes")}</p>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tr("Optional note for this order...")} className="cf-input" data-testid="order-notes" />
          </div>
        </div>
      </div>

      {!orderId && (
        <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-primary/30 bg-primary-light/40 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="order-recommendation-banner">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white"><Sparkles size={20} /></span>
            <div>
              <p className="font-heading text-base font-extrabold text-ink">{tr("Tomorrow's Order Recommendation")}</p>
              <p className="text-xs text-ink-mute">{tr("Based on ideal stock, last 7 days usage, current stock and goods in transit.")}</p>
            </div>
          </div>
          <Button onClick={loadRecommendation} loading={recLoading} className="shrink-0" data-testid="generate-recommendation-btn">
            <Sparkles size={16} /> {tr("Generate Recommended Order")}
          </Button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Product area */}
        <div className="min-w-0 flex-1">
          <div className="sticky top-14 z-10 -mx-1 mb-4 bg-app/90 px-1 py-2 backdrop-blur-md lg:top-16">
            <div className="relative mb-3">
              <Search size={18} className="absolute left-3.5 top-3 text-ink-mute" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tr("Search products instantly...")} className="cf-input pl-10" data-testid="product-search" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {cats.map((c) => {
                const sample = (products || []).find((p) => p.category_name === c);
                const cc = CATEGORY_COLORS[sample?.category_color] || CATEGORY_COLORS.packaging;
                const active = cat === c;
                return (
                  <button key={c} onClick={() => setCat(c)} data-testid={`cat-filter-${c}`}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${active ? "border-primary bg-primary text-white shadow-sm" : "border-line bg-white text-ink-soft hover:border-primary/50 hover:bg-primary-light"}`}>
                    {c !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: cc.text }} />}
                    {c === "all" ? tr("All Products") : c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} qty={cart[p.id] || 0} stock={stockMap[p.id]} setQty={setQty} />
            ))}
          </div>
          {filtered.length === 0 && <p className="py-16 text-center text-ink-mute">{tr("No products match your search.")}</p>}
        </div>

        {/* Desktop cart panel */}
        <div className="hidden w-80 shrink-0 lg:block xl:w-96">
          <div className="sticky top-24">
            <CartPanel lines={lines} totals={totals} setQty={setQty} onReview={() => setStep("review")} orderType={orderType} deliveryDate={deliveryDate} deliveryTime={deliveryTime} />
          </div>
        </div>
      </div>

      {/* Mobile/tablet sticky cart bar */}
      <div className="fixed bottom-16 left-0 right-0 z-20 border-t border-line bg-white/95 px-4 py-3 backdrop-blur-md lg:hidden">
        <button onClick={() => setCartOpen(true)} disabled={totals.items === 0} data-testid="open-cart-drawer"
          className="flex w-full items-center justify-between rounded-xl bg-primary px-4 py-3 text-white shadow-card transition-all active:scale-[0.99] disabled:opacity-50">
          <span className="flex items-center gap-2 font-semibold"><ShoppingCart size={18} /> {totals.items} {tr("items")}</span>
          <span className="flex items-center gap-2 font-bold">{fmtRp(totals.value)} <ArrowRight size={16} /></span>
        </button>
      </div>

      {/* Mobile cart drawer */}
      <Modal open={cartOpen} onClose={() => setCartOpen(false)} title={tr("Your Order")} size="sm">
        <CartPanel lines={lines} totals={totals} setQty={setQty} onReview={() => { setCartOpen(false); setStep("review"); }} orderType={orderType} deliveryDate={deliveryDate} deliveryTime={deliveryTime} embedded />
      </Modal>
    </div>
  );
}

/* ---------------- Sub-components ---------------- */
function ProductCard({ product: p, qty, stock, setQty }) {
  const { t } = useT();
  const cc = CATEGORY_COLORS[p.category_color] || CATEGORY_COLORS.packaging;
  const st = stock ? (STOCK_STATUS[stock.status] || STOCK_STATUS.SAFE) : null;
  const stTone = stock ? { SAFE: "bg-success-light text-success-text", LOW: "bg-warn-light text-warn-text", CRITICAL: "bg-danger-light text-danger-text" }[stock.status] : "";
  const suggested = stock ? Math.max(0, Math.round(((stock.ideal_stock || 0) - (stock.current_stock || 0)) * 100) / 100) : 0;
  return (
    <div className={`group flex flex-col overflow-hidden rounded-2xl border bg-white shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-cardhover ${qty > 0 ? "border-primary ring-2 ring-primary/25" : "border-line"}`} data-testid={`product-card-${p.name}`}>
      <div className="relative">
        <ProductAvatar image={p.image} colorKey={p.category_color} name={p.name} size="xl" className="rounded-b-none border-0" />
        <span className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm" style={{ background: cc.bg, color: cc.text }}>{p.category_name}</span>
        {stock && (
          <span className={`absolute right-2 top-2 rounded-md px-1.5 py-0.5 text-[10px] font-bold shadow-sm backdrop-blur-sm ${stTone}`} data-testid={`stock-badge-${p.name}`}>
            {t(st.label)}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-3">
        <p className="font-heading text-sm font-bold leading-tight text-ink line-clamp-1">{p.name}</p>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-ink-mute">{t("per")} {fmtUnit(p.unit)}</p>
          {stock
            ? <span className="text-[11px] font-semibold text-ink-soft">{t("Stock")} {fmtNum(stock.current_stock)} {fmtUnit(p.unit)}</span>
            : p.inventory_type === "PASSTHROUGH" && <Badge tone="info" className="text-[9px]">{t("Passthrough")}</Badge>}
        </div>
        {stock && suggested > 0 && (
          <button onClick={() => setQty(p.id, suggested)} data-testid={`use-suggested-${p.name}`}
            className="mb-2 flex items-center justify-between rounded-lg border border-dashed border-primary/40 bg-primary-light/60 px-2.5 py-1.5 text-left transition-colors hover:bg-primary-light">
            <span className="text-[10px] font-bold uppercase tracking-wide text-primary">{t("Suggested")} {fmtNum(suggested)} {fmtUnit(p.unit)}</span>
            <span className="text-[10px] font-bold text-primary underline">{t("Use")}</span>
          </button>
        )}
        <div className="mt-auto">
          {qty > 0 ? (
            <div className="flex items-center justify-between rounded-xl bg-primary-light p-1">
              <button onClick={() => setQty(p.id, qty - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm transition-transform active:scale-90" data-testid={`minus-${p.name}`}><Minus size={16} /></button>
              <div className="flex items-baseline gap-1">
                <input value={qty} onChange={(e) => setQty(p.id, Math.max(0, parseFloat(e.target.value) || 0))} className="w-10 bg-transparent text-center font-bold text-ink outline-none" data-testid={`qty-${p.name}`} />
                <span className="text-[10px] font-bold uppercase text-ink-mute">{fmtUnit(p.unit)}</span>
              </div>
              <button onClick={() => setQty(p.id, qty + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-white shadow-sm transition-transform active:scale-90" data-testid={`plus-${p.name}`}><Plus size={16} /></button>
            </div>
          ) : (
            <Button variant="secondary" size="sm" className="w-full" onClick={() => setQty(p.id, 1)} data-testid={`add-${p.name}`}><Plus size={16} /> {t("Add")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CartPanel({ lines, totals, setQty, onReview, orderType, deliveryDate, deliveryTime, embedded }) {
  const { t } = useT();
  const otLabel = { REGULAR: "Regular", URGENT: "Urgent", SPECIAL: "Special" }[orderType] || "Regular";
  const otTone = { REGULAR: "mute", URGENT: "danger", SPECIAL: "info" }[orderType] || "mute";
  const deliveryStr = deliveryDate ? `${deliveryDate}${deliveryTime ? " " + deliveryTime : ""}` : null;
  return (
    <div className={embedded ? "" : "overflow-hidden rounded-2xl border border-line bg-white shadow-card"}>
      {!embedded && (
        <div className="flex items-center gap-2 border-b border-line px-5 py-4">
          <ShoppingCart size={18} className="text-primary" />
          <h3 className="font-heading text-lg font-bold">{t("Your Order")}</h3>
          <span className="ml-auto rounded-full bg-primary-light px-2.5 py-0.5 text-xs font-bold text-primary">{totals.items}</span>
        </div>
      )}
      <div className={`${embedded ? "" : "max-h-[40vh]"} overflow-y-auto`} data-testid="cart-list">
        {lines.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <ShoppingCart size={36} className="mb-3 text-ink-mute/50" />
            <p className="text-sm font-semibold text-ink-soft">{t("Your cart is empty")}</p>
            <p className="text-xs text-ink-mute">{t("Tap products to add them here")}</p>
          </div>
        ) : lines.map((l) => (
          <div key={l.id} className="flex items-center gap-3 border-b border-line/60 px-4 py-3 last:border-0" data-testid={`cart-item-${l.name}`}>
            <ProductAvatar image={l.image} colorKey={l.category_color} name={l.name} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{l.name}</p>
              <p className="text-[11px] font-bold uppercase tracking-wider text-ink-mute">{fmtRp((l.price || 0) * l.qty)}</p>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg border border-line bg-app p-0.5">
              <button onClick={() => setQty(l.id, l.qty - 1)} className="flex h-6 w-6 items-center justify-center rounded text-primary transition-colors hover:bg-white" data-testid={`cart-minus-${l.name}`}><Minus size={13} /></button>
              <span className="w-7 text-center text-xs font-bold text-ink">{fmtNum(l.qty)}</span>
              <button onClick={() => setQty(l.id, l.qty + 1)} className="flex h-6 w-6 items-center justify-center rounded text-primary transition-colors hover:bg-white" data-testid={`cart-plus-${l.name}`}><Plus size={13} /></button>
            </div>
            <button onClick={() => setQty(l.id, 0)} className="rounded-lg p-1.5 text-ink-mute transition-colors hover:bg-danger-light hover:text-danger" data-testid={`cart-remove-${l.name}`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <div className="space-y-2.5 border-t border-line bg-app p-4" data-testid="cart-summary">
        <Row label={t("Total Items")} value={totals.items} testid="cart-total-items" />
        <Row label={t("Total Quantity")} value={fmtNum(totals.quantity)} testid="cart-total-qty" />
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-soft">{t("Order Type")}</span>
          <Badge tone={otTone}>{t(otLabel)}</Badge>
        </div>
        {deliveryStr && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-soft">{t("Requested Delivery")}</span>
            <span className="font-semibold text-ink" data-testid="cart-delivery">{deliveryStr}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-line pt-2.5">
          <span className="text-sm font-semibold text-ink-soft">{t("Estimated Value")}</span>
          <span className="font-heading text-xl font-bold text-primary" data-testid="cart-est-value">{fmtRp(totals.value)}</span>
        </div>
        <Button onClick={onReview} disabled={totals.items === 0} className="mt-1 w-full" data-testid="review-confirm-button">
          {t("Review & Confirm")} <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-mute"><Icon size={13} /> {label}</p>
      <p className="font-semibold text-ink">{value || "—"}</p>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-ink-soft">{label}</span>
      <span className="font-semibold text-ink" data-testid={testid}>{value}</span>
    </div>
  );
}
